import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from '@emailjs/browser';
import { Phone, Mail, ArrowRight, CheckCircle2, Sparkles, Clock, Shield, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// EmailJS configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_AUTOREPLY_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Page Hero
const PageHero = () => {
  return (
    <section className="pt-32 pb-20 bg-[#030303] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-1/3 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-[150px]" />
      </div>
      
      <div className="container mx-auto px-6 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-[0.65rem] text-[#D4AF37] tracking-[0.3em] uppercase mb-4">
            Kapcsolat
          </motion.p>
          <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-6">
            Beszéljünk a{" "}
            <span className="italic-accent text-gold-gradient">projektedről</span>
          </motion.h1>
          <motion.div variants={fadeInUp} className="gold-line mb-8" />
          <motion.p variants={fadeInUp} className="text-zinc-400 text-lg leading-relaxed">
            Töltsd ki az űrlapot, és 24 órán belül visszajelzek. 
            Vagy hívj fel, ha sürgős – mindig elérhető vagyok.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Form
const ContactForm = ({ onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || "Nincs megadva",
      company: formData.company || "Nincs megadva",
      project_type: formData.project_type || "Nincs megadva",
      message: formData.message,
      to_email: "stryxhungary@gmail.com",
    };

    try {
      // Send notification email to site owner
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      // Send auto-reply (non-blocking - don't fail the form if this errors)
      emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_AUTOREPLY_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      ).catch((err) => console.warn("Auto-reply failed:", err));
      
      setIsSubmitted(true);
      toast.success("Üzenet elküldve! Hamarosan jelentkezem.");
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        project_type: "",
        message: ""
      });
    } catch (error) {
      toast.error("Hiba történt az üzenet küldésekor. Kérlek próbáld újra.");
      console.error("EmailJS error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="premium-card p-12 text-center"
      >
        <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" strokeWidth={1} />
        <h3 className="font-['Cormorant_Garamond'] text-3xl text-white mb-4">
          Köszönöm az üzeneted!
        </h3>
        <p className="text-zinc-400 mb-8">
          24 órán belül felveszem veled a kapcsolatot.
        </p>
        <Button 
          className="btn-gold-outline"
          onClick={() => setIsSubmitted(false)}
        >
          Új üzenet küldése
        </Button>
      </motion.div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit} 
      className="premium-card p-8 md:p-10"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-3">
            Név *
          </label>
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="contact-input h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
            placeholder="Teljes neved"
            data-testid="contact-input-name"
          />
        </div>
        <div>
          <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-3">
            E-mail *
          </label>
          <Input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="contact-input h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
            placeholder="email@ceg.hu"
            data-testid="contact-input-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-3">
            Telefon
          </label>
          <Input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="contact-input h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
            placeholder="+36 30 123 4567"
            data-testid="contact-input-phone"
          />
        </div>
        <div>
          <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-3">
            Cégnév
          </label>
          <Input
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="contact-input h-12 bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
            placeholder="Cég neve (ha van)"
            data-testid="contact-input-company"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-3">
          Projekt típusa *
        </label>
        <Select 
          value={formData.project_type} 
          onValueChange={(value) => setFormData({ ...formData, project_type: value })}
        >
          <SelectTrigger 
            className="contact-input h-12 bg-white/[0.02] border-white/10 text-white"
            data-testid="contact-select-project"
          >
            <SelectValue placeholder="Válassz projekt típust" />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0a] border-white/10">
            <SelectItem value="weboldal" className="text-white hover:bg-white/5 focus:bg-white/5">Céges weboldal</SelectItem>
            <SelectItem value="landing" className="text-white hover:bg-white/5 focus:bg-white/5">Landing page</SelectItem>
            <SelectItem value="webshop" className="text-white hover:bg-white/5 focus:bg-white/5">Webshop</SelectItem>
            <SelectItem value="redesign" className="text-white hover:bg-white/5 focus:bg-white/5">Meglévő oldal újratervezése</SelectItem>
            <SelectItem value="egyeb" className="text-white hover:bg-white/5 focus:bg-white/5">Egyéb</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mb-8">
        <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-3">
          Üzenet *
        </label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          className="contact-input min-h-[150px] bg-white/[0.02] border-white/10 text-white resize-none placeholder:text-zinc-600"
          placeholder="Írd le röviden, mire van szükséged. Mi a céged? Mi a célod az oldallal? Van-e határidő?"
          data-testid="contact-input-message"
        />
      </div>

      <Button 
        type="submit" 
        className="btn-gold w-full"
        disabled={isSubmitting}
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? "Küldés folyamatban..." : "Üzenet küldése"}
        <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
      </Button>
    </form>
  );
};

// Contact Info
const ContactInfo = () => {
  const contactItems = [
    {
      icon: Phone,
      label: "Telefon",
      value: "+36 30 566 4154",
      href: "tel:+36305664154"
    },
    {
      icon: Mail,
      label: "E-mail",
      value: "stryxhungary@gmail.com",
      href: "mailto:stryxhungary@gmail.com"
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[0.65rem] text-[#D4AF37] tracking-[0.3em] uppercase mb-4">
          Elérhetőségek
        </p>
        <h3 className="font-['Cormorant_Garamond'] text-2xl text-white mb-6">
          Közvetlen kapcsolat
        </h3>
      </div>

      <div className="space-y-4">
        {contactItems.map((item, idx) => (
          <a
            key={idx}
            href={item.href}
            className="flex items-center gap-4 p-4 premium-card group"
            data-testid={`contact-${item.label.toLowerCase()}`}
          >
            <div className="w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#D4AF37]/50 transition-colors">
              <item.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{item.label}</p>
              <p className="text-white">{item.value}</p>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-8 pt-8 border-t border-white/5">
        <p className="text-zinc-500 text-sm leading-relaxed">
          <span className="text-white">Válaszidő:</span> Általában 24 órán belül válaszolok. 
          Sürgős esetben hívj bátran telefonon!
        </p>
      </div>
    </div>
  );
};

// Premium Promise Section - Reveals after form submission
const PremiumPromise = () => {
  const promises = [
    {
      icon: Clock,
      title: "24 órán belül válaszolok",
      desc: "Nem hagylak várakozni"
    },
    {
      icon: Sparkles,
      title: "Egyedi megoldások",
      desc: "Nincs sablon, csak Te"
    },
    {
      icon: Shield,
      title: "Teljes támogatás",
      desc: "A projekt után is melletted"
    },
    {
      icon: Award,
      title: "Minőségi garancia",
      desc: "100% elégedettség"
    }
  ];

  return (
    <motion.section 
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
      className="py-24 sm:py-32 bg-[#050505] relative overflow-hidden" 
      data-testid="premium-section"
    >
      {/* Elegant background */}
      <div className="absolute inset-0">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.03 }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]"
        >
          <div className="absolute inset-0 border border-[#D4AF37] rounded-full" />
          <div className="absolute inset-8 border border-[#D4AF37] rounded-full" />
          <div className="absolute inset-16 border border-[#D4AF37] rounded-full" />
          <div className="absolute inset-24 border border-[#D4AF37] rounded-full" />
        </motion.div>
      </div>

      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-6xl relative z-10">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-16 sm:mb-20"
          >
            <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.3em] uppercase mb-4">
              Köszönöm a bizalmad
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-white mb-6">
              Nem csak weboldalt kapsz.{" "}
              <span className="italic-accent text-gold-gradient">Partnert.</span>
            </h2>
            <div className="gold-line-center mt-6" />
          </motion.div>

          {/* Promise Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16 sm:mb-20">
            {promises.map((promise, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + idx * 0.15 }}
                className="group p-6 sm:p-8 border border-white/5 bg-white/[0.01] hover:border-[#D4AF37]/20 hover:bg-[#D4AF37]/[0.02] transition-all duration-500 text-center"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-4 sm:mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <promise.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-base sm:text-lg text-white mb-2">
                  {promise.title}
                </h3>
                <p className="text-zinc-500 text-xs sm:text-sm">
                  {promise.desc}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Elegant Quote */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="relative">
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[#D4AF37]/20 text-8xl font-serif">"</span>
              <blockquote className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl text-white italic leading-relaxed relative z-10">
                A legjobb befektetés, amit tehetsz, az a saját vállalkozásod online jelenlétébe fektetett energia.
              </blockquote>
              <div className="mt-8 flex items-center justify-center gap-4">
                <div className="w-12 h-px bg-[#D4AF37]/50" />
                <span className="text-[#D4AF37] text-sm tracking-wider">STRYX</span>
                <div className="w-12 h-px bg-[#D4AF37]/50" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Main Contact Page
export default function ContactPage() {
  const [showPremium, setShowPremium] = useState(false);
  const premiumRef = useRef(null);

  const handleFormSuccess = () => {
    setShowPremium(true);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <AnimatePresence mode="wait">
        {!showPremium ? (
          <motion.div
            key="form-section"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <PageHero />

            <section className="py-16 sm:py-20 bg-[#030303]">
              <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
                  {/* Contact Form - 2 columns */}
                  <div className="lg:col-span-2">
                    <ContactForm onSubmitSuccess={handleFormSuccess} />
                  </div>
                  
                  {/* Contact Info - 1 column */}
                  <div>
                    <ContactInfo />
                  </div>
                </div>
              </div>
            </section>
          </motion.div>
        ) : (
          <motion.div
            key="thank-you-section"
            ref={premiumRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <PremiumPromise />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
